import { Color3 } from '@babylonjs/core/Maths/math.color';
import { Vector3, Vector4 } from '@babylonjs/core/Maths/math.vector';
import { Mesh } from '@babylonjs/core/Meshes/mesh';
import { MeshBuilder } from '@babylonjs/core/Meshes/meshBuilder';
import { StandardMaterial } from '@babylonjs/core/Materials/standardMaterial';
import type { Texture } from '@babylonjs/core/Materials/Textures/texture';
import type { Scene } from '@babylonjs/core/scene';
import type { WorldStatics } from '@fisura/sim';
import { AVENUE, MAP_BOXES, TEAM_SPAWN, TUNNEL_FLOOR, buildStatics, type MapBox } from '@fisura/shared';
import {
  FACADE_TILE_X,
  FACADE_TILE_Y,
  FENCE_TILE_X,
  FENCE_TILE_Y,
  asphaltTexture,
  clearTextureCache,
  concreteTexture,
  facadeTexture,
  kioskTexture,
  riotFenceTexture,
} from './textures.js';
import { buildFacadeDecor } from './decor.js';
import { buildStreet } from './street.js';
import { buildSubwayDetail } from './train.js';

/** A shootable greybox dummy (legacy; live players are the targets now). */
export interface Target {
  mesh: Mesh;
  color: [number, number, number];
}

export interface WorldHandle {
  statics: WorldStatics;
  spawn: { x: number; z: number; yaw: number };
  targets: Target[];
  /** Merged structure meshes — the editor disposes + rebuilds these on edit. */
  boxMeshes: Mesh[];
}

const matCache = new Map<string, StandardMaterial>();

/** Materials shared by colour — keeps draw-call/material count flat. */
function flatMat(scene: Scene, name: string, c: [number, number, number]): StandardMaterial {
  const key = c.join(',');
  let m = matCache.get(key);
  if (!m) {
    m = new StandardMaterial(name, scene);
    m.diffuseColor = new Color3(c[0], c[1], c[2]);
    m.specularColor = new Color3(0.05, 0.05, 0.05);
    matCache.set(key, m);
  }
  return m;
}

/** A textured material keyed so all boxes of a kind share one (one draw call). */
function texMat(scene: Scene, key: string, tex: Texture): StandardMaterial {
  let m = matCache.get(key);
  if (!m) {
    m = new StandardMaterial(`mat-${key}`, scene);
    m.diffuseTexture = tex;
    m.specularColor = new Color3(0.04, 0.04, 0.04);
    matCache.set(key, m);
  }
  return m;
}

/** A building mass (plain colored wall) vs a labelled structural piece. */
function isBuildingMass(label: string | undefined): boolean {
  return label === undefined || label === 'building';
}

/** Uniform tiling for textured surfaces (asphalt, concrete). */
function uniformFaceUV(sx: number, sz: number, h: number, t: number): Vector4[] {
  const z = new Vector4(0, 0, sx / t, h / t);
  const x = new Vector4(0, 0, sz / t, h / t);
  const y = new Vector4(0, 0, sx / t, sz / t);
  return [z, z, x, x, y, y];
}

/** Fence tiling: pickets repeat along the wall length, one panel tall. */
function fenceFaceUV(sx: number, sz: number, h: number): Vector4[] {
  const z = new Vector4(0, 0, sx / FENCE_TILE_X, h / FENCE_TILE_Y); // long ±Z faces
  const x = new Vector4(0, 0, sz / FENCE_TILE_X, h / FENCE_TILE_Y); // long ±X faces
  const yCap = new Vector4(0.04, 0.06, 0.08, 0.1); // thin top/bottom: an opaque bar slice
  return [z, z, x, x, yCap, yCap];
}

const FENCE_LABELS = new Set(['spawn-screen', 'spawn-blocker']);
const KIOSK_LABELS = new Set(['kiosk', 'shelter']);

const FACADE_PLAIN = new Vector4(0.02, 0.02, 0.05, 0.05); // a plain-wall patch for non-window faces

/**
 * Window UV for a facade panel, WORLD-aligned (u from world-x, v from world-y)
 * so when the editor carves a panel into fragments the windows stay lined up.
 * Applied to the avenue-facing ±Z face; other faces get a plain patch.
 */
function facadeFaceUV(b: MapBox): Vector4[] {
  const x0 = b.cx - b.sx / 2;
  const x1 = b.cx + b.sx / 2;
  const y0 = b.y0 ?? 0;
  const win = new Vector4(x0 / FACADE_TILE_X, y0 / FACADE_TILE_Y, x1 / FACADE_TILE_X, (y0 + b.h) / FACADE_TILE_Y);
  // Avenue is toward z=0: north frontage (cz>0) shows its -Z face, south its +Z.
  return b.cz > 0
    ? [FACADE_PLAIN, win, FACADE_PLAIN, FACADE_PLAIN, FACADE_PLAIN, FACADE_PLAIN]
    : [win, FACADE_PLAIN, FACADE_PLAIN, FACADE_PLAIN, FACADE_PLAIN, FACADE_PLAIN];
}

/** Storefront texture on the 4 sides; plain (sign-band) patch on top/bottom. */
function kioskFaceUV(): Vector4[] {
  const f = new Vector4(0, 0, 1, 1);
  const cap = new Vector4(0.02, 0.01, 0.1, 0.05); // a slice of the red sign band
  return [f, f, f, f, cap, cap];
}

const CONCRETE_LABELS = new Set([
  'concrete', 'parapet', 'walkway', 'roof-ne', 'roof-sw', 'roof-w', 'terrace', 'roofstair',
]);

/**
 * Material for a box from its label. Building masses are PLAIN colored walls
 * (windows live on explicit facade panels in decor.ts, so they never leak onto
 * interior / ceiling / back faces). Street/concrete get tiled textures.
 */
function materialFor(
  scene: Scene,
  label: string | undefined,
  color: [number, number, number],
): StandardMaterial {
  if (isBuildingMass(label)) {
    // Tint is resolved into box.color in shared (so carve fragments keep it).
    return flatMat(scene, `wall-${color.join('_')}`, color);
  }
  if (FENCE_LABELS.has(label!)) {
    let m = matCache.get('fence');
    if (!m) {
      m = new StandardMaterial('mat-fence', scene);
      const tex = riotFenceTexture(scene);
      tex.hasAlpha = true;
      m.diffuseTexture = tex;
      m.useAlphaFromDiffuseTexture = true;
      m.transparencyMode = StandardMaterial.MATERIAL_ALPHATEST; // crisp cutout, no sort issues
      m.backFaceCulling = false; // thin fence — visible from both sides
      m.specularColor = new Color3(0.12, 0.12, 0.14);
      matCache.set('fence', m);
    }
    return m;
  }
  if (label?.startsWith('facade-')) {
    const v = Number(label.slice('facade-'.length)) || 0;
    const key = label;
    let m = matCache.get(key);
    if (!m) {
      const tex = facadeTexture(scene, v);
      tex.hasAlpha = true;
      m = new StandardMaterial(`mat-${key}`, scene);
      m.diffuseTexture = tex;
      m.specularColor = new Color3(0.04, 0.04, 0.04);
      m.transparencyMode = StandardMaterial.MATERIAL_ALPHATEST; // crisp cutout — no depth-sort issues
      matCache.set(key, m);
    }
    return m;
  }
  if (KIOSK_LABELS.has(label!)) return texMat(scene, 'kiosk', kioskTexture(scene));
  if (label === 'street') return texMat(scene, 'asphalt', asphaltTexture(scene));
  if (CONCRETE_LABELS.has(label!)) return texMat(scene, 'concrete', concreteTexture(scene));
  return flatMat(scene, 'flat', color);
}

/**
 * Build the greybox scene meshes from the shared map data. Collision statics
 * come from the same source the server uses ({@link buildStatics}), so visuals
 * and authoritative simulation stay in lockstep. All boxes sharing a material
 * are MERGED into one mesh — the whole ~300-box map renders in ~10 draw calls.
 */
export function buildWorld(scene: Scene): WorldHandle {
  matCache.clear(); // fresh materials per scene (HMR safety)
  clearTextureCache();
  const len = AVENUE.north - AVENUE.south;
  const midZ = (AVENUE.south + AVENUE.north) / 2;

  // The visual "deep floor" — this is the subte track level; the street
  // surface is built from slab boxes (with the stairwell holes) above it.
  const ground = MeshBuilder.CreateGround(
    'ground',
    { width: AVENUE.halfWidth * 2 + 24, height: len + 20, subdivisions: 8 },
    scene,
  );
  ground.position = new Vector3(0, TUNNEL_FLOOR, midZ);
  ground.material = flatMat(scene, 'ground-mat', [0.18, 0.18, 0.22]);
  ground.freezeWorldMatrix();

  const boxMeshes = buildBoxMeshes(scene, MAP_BOXES);

  // Protruding facade articulation (balconies, cornices, ground-floor shops)
  // along the building frontages — turns the textured masses into apartment
  // blocks. Decorative only (no colliders).
  buildFacadeDecor(scene);

  // Street dressing: sidewalks, curbs, road markings, trees, cars + bus.
  buildStreet(scene);

  // Subte: the stationed "A FLORES" train + station signage / lighting.
  buildSubwayDetail(scene);

  return { statics: buildStatics(), spawn: { ...TEAM_SPAWN[0]! }, targets: [], boxMeshes };
}

/**
 * Build the collision/structure box meshes (everything from the box list except
 * the client-drawn vehicles/trees/train), grouped + merged by material. Returns
 * the meshes so the editor can dispose + rebuild them after a live map edit.
 */
export function buildBoxMeshes(scene: Scene, boxes: MapBox[]): Mesh[] {
  const groups = new Map<StandardMaterial, Mesh[]>();
  for (let i = 0; i < boxes.length; i++) {
    const b = boxes[i]!;
    if (b.label === 'car' || b.label === 'bus' || b.label === 'tree') continue;
    if (b.label?.startsWith('train-')) continue;
    const mat = materialFor(scene, b.label, b.color);
    const isFence = FENCE_LABELS.has(b.label!);
    // Fences render visually thin (a riot barrier), though their collider keeps
    // the full footprint so navigation lanes + spawn screening are unchanged.
    const sx = isFence && b.sx < b.sz ? Math.min(b.sx, 0.3) : b.sx;
    const sz = isFence && b.sz <= b.sx ? Math.min(b.sz, 0.3) : b.sz;
    const opts: Parameters<typeof MeshBuilder.CreateBox>[1] = {
      width: sx,
      depth: sz,
      height: b.h,
      wrap: true,
    };
    if (isFence) opts.faceUV = fenceFaceUV(sx, sz, b.h);
    else if (b.label?.startsWith('facade-')) opts.faceUV = facadeFaceUV(b);
    else if (KIOSK_LABELS.has(b.label!)) opts.faceUV = kioskFaceUV();
    else if (mat.diffuseTexture) opts.faceUV = uniformFaceUV(b.sx, b.sz, b.h, 4);
    const mesh = MeshBuilder.CreateBox(`box-${b.label ?? i}`, opts, scene);
    mesh.position = new Vector3(b.cx, (b.y0 ?? 0) + b.h / 2, b.cz);
    mesh.material = mat;
    const list = groups.get(mat);
    if (list) list.push(mesh);
    else groups.set(mat, [mesh]);
  }
  const out: Mesh[] = [];
  for (const [mat, meshes] of groups) {
    if (meshes.length === 1) {
      meshes[0]!.freezeWorldMatrix();
      out.push(meshes[0]!);
      continue;
    }
    const merged = Mesh.MergeMeshes(meshes, true, true);
    if (merged) {
      merged.material = mat;
      merged.isPickable = true;
      merged.freezeWorldMatrix();
      out.push(merged);
    }
  }
  return out;
}
